import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import AutobazarListings from '../components/AutobazarListings';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003';

const PageTitle = styled.h1`
  font-size: 32px;
  margin-bottom: 10px;
`;

const SubTitle = styled.h2`
  font-size: 18px;
  color: #666;
  margin-bottom: 20px;
`;

const VehicleCountHeading = styled.h3`
  font-size: 20px;
  color: #333;
  margin: 30px 0 20px 0;
  font-weight: 600;
`;

const IntervalContainer = styled.div`
  display: flex;
  margin-bottom: 30px;
`;

const IntervalButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  padding: 10px 20px;
  background-color: ${props => {
    if (props.disabled) return '#f0f0f0';
    return props.active ? '#333' : 'white';
  }};
  color: ${props => {
    if (props.disabled) return '#999';
    return props.active ? 'white' : '#333';
  }};
  border: 1px solid ${props => props.disabled ? '#e0e0e0' : '#ccc'};
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  outline: none;
  margin-right: 5px;
  min-width: 100px;
  transition: all 0.3s;
  border-radius: 25px;
  opacity: ${props => props.disabled ? 0.7 : 1};

  &:first-child {
    border-top-left-radius: 25px;
    border-bottom-left-radius: 25px;
  }

  &:last-child {
    border-top-right-radius: 25px;
    border-bottom-right-radius: 25px;
    margin-right: 0;
  }
`;

const RefreshButton = styled.button<{ active?: boolean }>`
  background-color: ${props => props.active ? '#666' : '#C52126'};
  color: white;
  border: none;
  border-radius: 25px;
  padding: 12px 20px;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  margin-left: 10px;

  &:hover {
    background-color: ${props => props.active ? '#555' : '#A61D22'};
  }
`;

const CarListPage: React.FC = () => {
  const [activeInterval, setActiveInterval] = useState<number | 'off'>(3);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [listingsCount, setListingsCount] = useState<number>(0);
  const [activeListingsCount, setActiveListingsCount] = useState<number>(0);

  // Fetch user settings on component mount
  useEffect(() => {
    async function fetchUserSettings() {
      try {
        setIsLoading(true);
        
        // Get the username from localStorage
        let username = '';
        const storedUser = localStorage.getItem('user');
        
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            username = parsedUser.username || '';
          } catch (err) {
            console.error('Error parsing stored user:', err);
          }
        }
        
        // Fallback to direct username if necessary
        if (!username) {
          username = localStorage.getItem('username') || '';
        }
        
        // Fallback to a test username if we still don't have one
        if (!username) {
          username = 'mtautos';
        }
        
        // Fetch user settings
        const response = await axios.get(`${API_URL}/api/user-settings/${username}`);
        
        if (response.data && response.data.success) {
          // Set the active interval based on the response
          setActiveInterval(response.data.update_interval);
          
          // Set disabled state based on whether interval is 'off'
          setIsDisabled(response.data.update_interval === 'off');
        } else {
          console.error('Failed to fetch user settings:', response.data.message);
        }
      } catch (err) {
        console.error('Error fetching user settings:', err);
        setError('Failed to load user settings');
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchUserSettings();
  }, []);
  
  // Update user settings when interval changes
  const updateUserSettings = async (interval: number | 'off') => {
    try {
      // Get the username from localStorage
      let username = '';
      const storedUser = localStorage.getItem('user');
      
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          username = parsedUser.username || '';
        } catch (err) {
          console.error('Error parsing stored user:', err);
        }
      }
      
      // Fallback to direct username if necessary
      if (!username) {
        username = localStorage.getItem('username') || '';
      }
      
      // Fallback to a test username if we still don't have one
      if (!username) {
        username = 'mtautos';
      }
      
      // Update user settings
      const response = await axios.post(`${API_URL}/api/update-interval`, {
        username,
        interval
      });
      
      if (!response.data || !response.data.success) {
        console.error('Failed to update user settings:', response.data?.message);
      }
    } catch (err) {
      console.error('Error updating user settings:', err);
    }
  };
  
  const handleRefreshToggle = () => {
    const newDisabledState = !isDisabled;
    setIsDisabled(newDisabledState);
    
    // If disabling, save 'off' to the database
    // If enabling, save the last active interval (or default to 3)
    const newInterval = newDisabledState ? 'off' : (activeInterval === 'off' ? 3 : activeInterval);
    setActiveInterval(newInterval);
    updateUserSettings(newInterval);
  };
  
  const handleIntervalClick = (days: number) => {
    if (!isDisabled) {
      setActiveInterval(days);
      updateUserSettings(days);
    }
  };
  
  if (isLoading) {
    return <div>Loading settings...</div>;
  }
  
  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div>
      <PageTitle>Moje autá</PageTitle>
      <SubTitle>Interval vymazania a opätovného nahrania</SubTitle>
      
      <IntervalContainer>
        <IntervalButton 
          active={activeInterval === 3} 
          onClick={() => handleIntervalClick(3)}
          disabled={isDisabled}
        >
          3 dni
        </IntervalButton>
        <IntervalButton 
          active={activeInterval === 4} 
          onClick={() => handleIntervalClick(4)}
          disabled={isDisabled}
        >
          4 dni
        </IntervalButton>
        <IntervalButton 
          active={activeInterval === 5} 
          onClick={() => handleIntervalClick(5)}
          disabled={isDisabled}
        >
          5 dni
        </IntervalButton>
        <IntervalButton 
          active={activeInterval === 7} 
          onClick={() => handleIntervalClick(7)}
          disabled={isDisabled}
        >
          7 dni
        </IntervalButton>
        <IntervalButton 
          active={activeInterval === 14} 
          onClick={() => handleIntervalClick(14)}
          disabled={isDisabled}
        >
          14 dni
        </IntervalButton>
        <RefreshButton 
          onClick={handleRefreshToggle}
          active={isDisabled}
        >
          {isDisabled ? 'Zapnúť' : 'Vypnúť'}
        </RefreshButton>
      </IntervalContainer>
      
      <VehicleCountHeading>
        Počet vozidiel: {listingsCount} | Aktívne vozidlá: {activeListingsCount}
      </VehicleCountHeading>
      
      {/* Display vehicle listings */}
      <AutobazarListings 
        onListingsChange={setListingsCount} 
        onActiveListingsChange={setActiveListingsCount} 
      />
    </div>
  );
};

export default CarListPage; 